import { useState } from "react";
import { Plus, Folder, ChevronRight, ChevronDown, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useChatPlusStore } from "../store/chatPlusStore";
import * as api from "../services/api";
import { toast } from "sonner";

export function FolderSidebar() {
  const { folders, activeFolderId, setActiveFolderId, setFolders, addFolder, updateFolderInStore, removeFolder } = useChatPlusStore();
  const [isCreating, setIsCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      const folder = await api.createFolder(newFolderName);
      addFolder(folder);
      setNewFolderName("");
      setIsCreating(false);
      toast.success("Pasta criada com sucesso");
    } catch (error) {
      console.error("Erro ao criar pasta:", error);
      toast.error("Erro ao criar pasta");
    }
  };

  const handleUpdateFolder = async (id: string) => {
    if (!editingName.trim()) return;
    
    try {
      await api.updateFolder(id, editingName);
      updateFolderInStore(id, editingName);
      setEditingId(null);
      toast.success("Pasta renomeada");
    } catch (error) {
      console.error("Erro ao renomear pasta:", error);
      toast.error("Erro ao renomear pasta");
    }
  };

  const handleDeleteFolder = async (id: string) => {
    try {
      await api.deleteFolder(id);
      removeFolder(id);
      toast.success("Pasta excluída");
    } catch (error) {
      console.error("Erro ao excluir pasta:", error);
      toast.error("Erro ao excluir pasta");
    }
  };

  return (
    <div className="flex flex-col h-full border-r bg-background">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Pastas</h2>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsCreating(true)}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {isCreating && (
          <div className="flex gap-2">
            <Input
              placeholder="Nome da pasta"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              autoFocus
            />
            <Button size="sm" onClick={handleCreateFolder}>
              OK
            </Button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {folders.map((folder) => (
          <div key={folder.id} className="mb-1">
            <div
              className={`flex items-center justify-between p-2 rounded-lg hover:bg-accent cursor-pointer ${
                activeFolderId === folder.id ? 'bg-accent' : ''
              }`}
              onClick={() => setActiveFolderId(folder.id)}
            >
              <div className="flex items-center gap-2 flex-1">
                {activeFolderId === folder.id ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                <Folder className="w-4 h-4" />
                
                {editingId === folder.id ? (
                  <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdateFolder(folder.id)}
                    onBlur={() => handleUpdateFolder(folder.id)}
                    className="h-6 py-0"
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span className="text-sm truncate flex-1">{folder.name}</span>
                )}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                    <MoreVertical className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingId(folder.id);
                      setEditingName(folder.name);
                    }}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Renomear
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFolder(folder.id);
                    }}
                    className="text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Excluir
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
